import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircleIcon,
  CloudUploadIcon,
  DocumentIcon,
  XIcon,
} from "@heroicons/react/outline";

import ApiService from "../../services/ApiService";
import EasyTrans from "../bundel/EasyTrans";
import FormatService from "../../services/FormatService";
import Spinner from "../bundel/Spinner";
import { FormBlock } from "../ui";
import { useDropzone } from "react-dropzone";

const RegisterWorkFileUpload = (props) => {
  // When the page arrived from the Nextcloud plugin (props.alreadyUploaded),
  // the file is already in fairregister storage. Start in the "uploaded"
  // state so the confirmation shows the filename instead of an empty dropzone.
  const [fileUploaded, setFileUploaded] = useState(!!props.alreadyUploaded);
  const [filesize, setFilesize] = useState("");
  const [loading, setLoading] = useState(false);

  // Parent flips `alreadyUploaded` asynchronously after mount (when arriving
  // from the Nextcloud plugin with prefix+filename or fromUrl+filename in
  // the URL). useState only takes the initial value once, so mirror prop
  // changes into local state to actually switch into the "uploaded" view.
  useEffect(() => {
    if (props.alreadyUploaded) {
      setFileUploaded(true);
    }
  }, [props.alreadyUploaded]);

  const onDrop = useCallback((acceptedFiles) => {
    setLoading(true);
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onabort = () => console.log("file reading was aborted");
      reader.onerror = () => console.log("file reading has failed");
      reader.onload = () => {
        props.onSetFile(reader.result);
      };
      reader.readAsArrayBuffer(file);
      props.onSetFilename(file.name);

      ApiService.uploadWork(
        file.name,
        file,
        props.onSetPresignedURL,
        props.onSetPrefix,
        setLoading,
      );

      setFilesize(FormatService.formatBytesSizeUnits(file.size));
      setFileUploaded(true);
      props.setFileError(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const removeFile = () => {
    props.onSetPresignedURL("");
    props.onSetPrefix("");
    props.onSetFilename("");
    props.onSetFile("");
    setFilesize("");
    setFileUploaded(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 sm:my-6 lg:my-10">
      <FormBlock
        title={<EasyTrans>RegisterWork.Title.UploadWork</EasyTrans>}
        description={<EasyTrans>RegisterWork.Title.UploadWorkCaption</EasyTrans>}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <Spinner />
            <p className="text-sm text-gray-500">
              <EasyTrans>RegisterWork.FileUpload.UploadFile</EasyTrans>…
            </p>
          </div>
        ) : fileUploaded ? (
          <div>
            {props.alreadyUploaded && (
              <p className="mb-3 text-sm text-gray-500">
                <EasyTrans>
                  RegisterWork.FileUpload.UploadedExternally
                </EasyTrans>
              </p>
            )}
            <div className="flex items-center gap-3 rounded-xl border border-fairdarkgreen/40 bg-fairdarkgreen/5 p-4">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-fairdarkgreen/10 text-fairdarkgreen">
                <DocumentIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 font-medium text-gray-900">
                  <CheckCircleIcon
                    className="h-4 w-4 flex-shrink-0 text-fairdarkgreen"
                    aria-hidden="true"
                  />
                  <span className="truncate">{props.filename}</span>
                </p>
                {filesize && (
                  <p className="text-xs text-gray-500">{filesize}</p>
                )}
              </div>
              {!props.alreadyUploaded && (
                <button
                  type="button"
                  onClick={removeFile}
                  aria-label="Remove file"
                  title="Remove file"
                  className="flex-shrink-0 rounded-md p-2 text-gray-400 hover:bg-white hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-fairblue-500"
                >
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div
              {...getRootProps()}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
                props.fileError
                  ? "border-red-500 bg-red-50"
                  : isDragActive
                    ? "border-fairdarkgreen bg-fairdarkgreen/5"
                    : "border-gray-300 hover:border-fairblue-500 hover:bg-fairgray"
              }`}
            >
              <input
                id="file-upload"
                name="file-upload"
                {...getInputProps()}
                onClick={() => props.setFileError(false)}
              />
              <CloudUploadIcon
                className="h-10 w-10 text-gray-400"
                aria-hidden="true"
              />
              <p className="text-sm text-gray-600">
                <span className="font-medium text-fairblue-500">
                  <EasyTrans>RegisterWork.FileUpload.UploadFile</EasyTrans>
                </span>{" "}
                <EasyTrans>RegisterWork.FileUpload.UploadFileDragDrop</EasyTrans>
              </p>
              <p className="text-xs text-gray-400">
                <EasyTrans>RegisterWork.FileUpload.MaxFileSize</EasyTrans>
              </p>
            </div>
            {props.fileError && (
              <span className="mt-2 block text-sm text-red-700">
                <EasyTrans>RegisterWork.FileUpload.ChooseFile</EasyTrans>
              </span>
            )}
          </div>
        )}
      </FormBlock>
    </div>
  );
};

export default RegisterWorkFileUpload;
